import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { 
  Bitcoin, 
  TrendingUp, 
  DollarSign, 
  Activity,
  LogOut,
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  Package,
  Users,
  Tablet,
  Code,
  Link as LinkIcon,
  ExternalLink,
  History,
  Settings,
  Shield,
  Percent,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import PaymentModal from '../components/PaymentModal';
import PaymentHistory from '../components/PaymentHistory';
import MerchantAnalytics from '../components/MerchantAnalytics';
import './Dashboard.css';

const Dashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cloverStatus, setCloverStatus] = useState(null);
  const [loadingClover, setLoadingClover] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [activeTab, setActiveTab] = useState('payments'); // 'overview' or 'payments' or 'settings'
  const [payoutBalance, setPayoutBalance] = useState(null);
  const [merchantSettings, setMerchantSettings] = useState(null);
  const [conversionBuffer, setConversionBuffer] = useState('0');
  const [savingBuffer, setSavingBuffer] = useState(false);
  const [volatilityData, setVolatilityData] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const navigate = useNavigate();

  // WebSocket for real-time payment updates
  useEffect(() => {
    if (!user?.id) return;
    
    const token = localStorage.getItem('token');
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsHost = backendUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
    const wsUrl = `${wsProtocol}//${wsHost}/ws/payments/${user.id}?token=${token}`;
    
    let ws = null;
    let reconnectTimeout = null;
    
    const connectWebSocket = () => {
      try {
        ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log('WebSocket connected for real-time updates');
          setWsConnected(true);
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'payment_update') {
              // Update payment status in real-time
              toast.success(`Payment ${data.payment_id.slice(0, 8)}... status: ${data.status}`);
              // Refresh data to show updated payment
              fetchData();
              fetchPayoutBalance();
            }
          } catch (e) {
            // Handle ping/pong
            if (event.data === 'pong') return;
          }
        };
        
        ws.onclose = () => {
          setWsConnected(false);
          // Attempt to reconnect after 5 seconds
          reconnectTimeout = setTimeout(connectWebSocket, 5000);
        };
        
        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          setWsConnected(false);
        };
        
        // Send ping every 30 seconds to keep connection alive
        const pingInterval = setInterval(() => {
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);
        
        return () => clearInterval(pingInterval);
      } catch (error) {
        console.error('WebSocket connection error:', error);
      }
    };
    
    connectWebSocket();
    
    return () => {
      if (ws) ws.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
    // Re-connect only when the user identity changes — adding fetchData/fetchPayoutBalance
    // here would re-establish the socket on every state update, breaking realtime updates.
    // eslint-disable-next-line
  }, [user?.id]);

  useEffect(() => {
    // If user is super admin with role 'super_admin', redirect to super admin portal
    if (user?.is_super_admin && user?.role === 'super_admin') {
      navigate('/super-admin');
      return;
    }
    
    fetchData();
    fetchCloverStatus();
    fetchPayoutBalance();
    fetchMerchantSettings();
    fetchVolatilityData();
    // Fetchers read from localStorage at call time; including them as deps would
    // require useCallback wrappers everywhere and offer no behavioral benefit.
    // eslint-disable-next-line
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, transRes] = await Promise.all([
        axios.get(`${API}/transactions/stats`, { headers }),
        axios.get(`${API}/transactions`, { headers })
      ]);
      
      setStats(statsRes.data);
      setTransactions(transRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCloverStatus = async () => {
    try {
      setLoadingClover(true);
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/clover/connection-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCloverStatus(data);
      }
    } catch (error) {
      console.error('Error fetching Clover status:', error);
    } finally {
      setLoadingClover(false);
    }
  };

  const fetchMerchantSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/merchant/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMerchantSettings(data);
        setConversionBuffer(data.conversion_rate_buffer?.toString() || '0');
      }
    } catch (error) {
      console.error('Error fetching merchant settings:', error);
    }
  };

  const fetchVolatilityData = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/crypto/volatility`);
      
      if (response.ok) {
        const data = await response.json();
        setVolatilityData(data);
      }
    } catch (error) {
      console.error('Error fetching volatility data:', error);
    }
  };

  const applyRecommendedBuffer = () => {
    if (volatilityData?.recommended_buffer) {
      setConversionBuffer(volatilityData.recommended_buffer.toString());
      toast.info(`Applied recommended buffer: ${volatilityData.recommended_buffer}%`);
    }
  };

  const handleUpdateConversionBuffer = async () => {
    try {
      setSavingBuffer(true);
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${BACKEND_URL}/api/merchant/settings/conversion-buffer`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ conversion_rate_buffer: parseFloat(conversionBuffer) || 0 })
      });
      
      if (response.ok) {
        toast.success(`Conversion buffer updated to ${conversionBuffer}%`);
        fetchMerchantSettings();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update');
      }
    } catch (error) {
      console.error('Error updating conversion buffer:', error);
      toast.error('Failed to update conversion buffer');
    } finally {
      setSavingBuffer(false);
    }
  };

  const fetchPayoutBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/merchant/balance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPayoutBalance(data);
      }
    } catch (error) {
      console.error('Error fetching payout balance:', error);
    }
  };

  const handleConnectClover = async () => {
    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/clover/oauth/authorize`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Clover OAuth page
        window.location.href = data.authorization_url;
      }
    } catch (error) {
      console.error('Error connecting Clover:', error);
      alert('Failed to connect Clover account');
    }
  };

  const handleCreatePayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const origin = window.location.origin;
      
      const response = await axios.post(
        `${API}/payments/create-session`,
        {
          amount: parseFloat(paymentAmount),
          currency: 'usd',
          success_url: `${origin}/dashboard?session_id={{CHECKOUT_SESSION_ID}}`,
          cancel_url: `${origin}/dashboard`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create payment session';
      
      if (errorMsg.includes('Payment') || errorMsg.includes('not configured')) {
        toast.error('Payment service not configured. Please contact support to enable cryptocurrency payments.', {
          duration: 6000
        });
      } else {
        toast.error(errorMsg);
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={18} className="status-icon-success" />;
      case 'pending':
        return <Clock size={18} className="status-icon-pending" />;
      case 'failed':
        return <XCircle size={18} className="status-icon-error" />;
      default:
        return <Activity size={18} />;
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-page" data-testid="dashboard-page">
      {/* Header */}
      <header className="dashboard-header" data-testid="dashboard-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo" data-testid="dashboard-logo">
              <Bitcoin size={32} />
              <span>Crypto Cashier</span>
            </div>
          </div>
          <div className="header-right">
            <div className="user-info" data-testid="user-info">
              <span className="user-name" data-testid="user-name">{user?.business_name}</span>
              <span className="user-email" data-testid="user-email">{user?.email}</span>
            </div>
            <button 
              className="btn btn-secondary" 
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content" data-testid="dashboard-content">
        <div className="dashboard-container">
          {/* Setup Banner */}
          <div className="setup-banner" data-testid="setup-banner" style={{background: 'rgba(34, 197, 94, 0.1)', borderColor: 'rgba(34, 197, 94, 0.3)'}}>
            <div className="setup-banner-icon">
              <Bitcoin size={24} />
            </div>
            <div className="setup-banner-content">
              <div className="setup-banner-title" style={{color: '#22c55e'}}>✅ Crypto Payment System Ready - Create Your First Payment!</div>
              <div className="setup-banner-text">
                Your payment system is live and ready to accept cryptocurrency payments ✅
                <br/>
                <strong>Ready to test:</strong> Click "Create Payment Link" below to process your first crypto payment!
                <br/>
                <small>Customers can pay with Bitcoin, Ethereum, Litecoin, USDT, USDC, and 300+ other cryptocurrencies</small>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="stats-grid" data-testid="stats-grid">
            <div className="stat-card" data-testid="stat-card-revenue">
              <div className="stat-icon" style={{background: 'rgba(74, 222, 128, 0.1)'}}>
                <DollarSign size={24} color="#4ade80" />
              </div>
              <div className="stat-info">
                <p className="stat-label" data-testid="stat-label-revenue">Total Revenue</p>
                <p className="stat-value" data-testid="stat-value-revenue">${stats?.total_revenue?.toFixed(2) || '0.00'}</p>
              </div>
            </div>

            <div className="stat-card" data-testid="stat-card-transactions">
              <div className="stat-icon" style={{background: 'rgba(96, 165, 250, 0.1)'}}>
                <TrendingUp size={24} color="#60a5fa" />
              </div>
              <div className="stat-info">
                <p className="stat-label" data-testid="stat-label-transactions">Total Transactions</p>
                <p className="stat-value" data-testid="stat-value-transactions">{stats?.total_transactions || 0}</p>
              </div>
            </div>

            <div className="stat-card" data-testid="stat-card-successful">
              <div className="stat-icon" style={{background: 'rgba(34, 197, 94, 0.1)'}}>
                <CheckCircle size={24} color="#22c55e" />
              </div>
              <div className="stat-info">
                <p className="stat-label" data-testid="stat-label-successful">Successful</p>
                <p className="stat-value" data-testid="stat-value-successful">{stats?.successful_payments || 0}</p>
              </div>
            </div>

            <div className="stat-card" data-testid="stat-card-pending">
              <div className="stat-icon" style={{background: 'rgba(251, 191, 36, 0.1)'}}>
                <Clock size={24} color="#fbbf24" />
              </div>
              <div className="stat-info">
                <p className="stat-label" data-testid="stat-label-pending">Pending</p>
                <p className="stat-value" data-testid="stat-value-pending">{stats?.pending_payments || 0}</p>
              </div>
            </div>
          </div>

          {/* Payout Balance Section */}
          {payoutBalance && (
            <div className="payout-balance-section" data-testid="payout-balance-section">
              <div className="payout-balance-card">
                <div className="payout-balance-header">
                  <DollarSign size={20} />
                  <h3>Payout Balance</h3>
                </div>
                <div className="payout-balance-grid">
                  <div className="payout-stat">
                    <span className="payout-stat-label">Pending Payout</span>
                    <span className="payout-stat-value pending">${payoutBalance.pending_balance?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="payout-stat">
                    <span className="payout-stat-label">Processing</span>
                    <span className="payout-stat-value processing">${payoutBalance.processing_balance?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="payout-stat">
                    <span className="payout-stat-label">Total Paid Out</span>
                    <span className="payout-stat-value success">${payoutBalance.total_paid_out?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="payout-stat">
                    <span className="payout-stat-label">Total Earned</span>
                    <span className="payout-stat-value">${payoutBalance.total_earned?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
                {payoutBalance.last_payout_date && (
                  <div className="last-payout-info">
                    <small>Last payout: ${payoutBalance.last_payout_amount?.toFixed(2)} on {new Date(payoutBalance.last_payout_date).toLocaleDateString()}</small>
                  </div>
                )}
                {!payoutBalance.bank_verified && payoutBalance.pending_balance > 0 && (
                  <div className="bank-verification-notice">
                    <small>⚠️ Bank account not verified. Contact support to verify your bank and receive payouts.</small>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-section" data-testid="action-section">
            <button 
              className="btn btn-success btn-large"
              onClick={() => navigate('/pos')}
              data-testid="open-pos-btn"
            >
              <Tablet size={20} /> Open POS
            </button>
            <button 
              className="btn btn-primary btn-large"
              onClick={() => setShowPaymentModal(true)}
              data-testid="create-payment-btn"
            >
              <CreditCard size={20} /> Create Payment Link
            </button>
            <button 
              className="btn btn-secondary btn-large"
              onClick={() => navigate('/financials')}
              data-testid="view-financials-btn"
            >
              <TrendingUp size={20} /> View Financials
            </button>
            {user?.role === 'owner' && (
              <>
                <button 
                  className="btn btn-secondary btn-large"
                  onClick={() => navigate('/products')}
                  data-testid="manage-products-btn"
                >
                  <Package size={20} /> Manage Products
                </button>
                <button 
                  className="btn btn-secondary btn-large"
                  onClick={() => navigate('/cashiers')}
                  data-testid="manage-cashiers-btn"
                >
                  <Users size={20} /> Manage Cashiers
                </button>
                <button 
                  className="btn btn-secondary btn-large"
                  onClick={() => navigate('/widget')}
                  data-testid="website-integration-btn"
                >
                  <Code size={20} /> Website Integration
                </button>
                
                {/* Clover Integration Section */}
                <div className="clover-integration-section">
                  <h3><LinkIcon size={20} /> Clover POS Integration</h3>
                  {loadingClover ? (
                    <p>Loading Clover status...</p>
                  ) : cloverStatus && cloverStatus.connected ? (
                    <div className="clover-connected">
                      <CheckCircle size={20} className="icon-success" />
                      <div className="clover-info">
                        <p><strong>Connected:</strong> {cloverStatus.merchant_name || 'Clover Account'}</p>
                        <p className="clover-id">Merchant ID: {cloverStatus.merchant_id}</p>
                        <p className="clover-date">Connected: {new Date(cloverStatus.connected_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="clover-not-connected">
                      <p>Connect your Clover POS to accept cryptocurrency payments for in-person transactions</p>
                      <button 
                        className="btn btn-primary"
                        onClick={handleConnectClover}
                      >
                        <ExternalLink size={18} /> Connect Clover Account
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
            {user?.is_agent && (
              <button 
                className="btn btn-success btn-large"
                onClick={() => navigate('/agent')}
                data-testid="agent-dashboard-btn"
              >
                <Users size={20} /> Agent Dashboard
              </button>
            )}
            {user?.is_admin && (
              <>
                <button 
                  className="btn btn-admin btn-large"
                  onClick={() => navigate('/admin')}
                  data-testid="admin-dashboard-btn"
                >
                  <Activity size={20} /> Admin Dashboard
                </button>
                <button 
                  className="btn btn-admin btn-large"
                  onClick={() => navigate('/admin/manage')}
                  data-testid="admin-manage-btn"
                >
                  <Activity size={20} /> Platform Management
                </button>
              </>
            )}
            {user?.is_super_admin && (
              <button 
                className="btn btn-super-admin btn-large"
                onClick={() => navigate('/super-admin')}
                data-testid="super-admin-dashboard-btn"
              >
                <Activity size={20} /> Super Admin Portal
              </button>
            )}
          </div>

          {/* Compact analytics on overview */}
          <MerchantAnalytics compact />

          {/* Dashboard Tabs */}
          <div className="dashboard-tabs" data-testid="dashboard-tabs">
            <button 
              className={`tab-btn ${activeTab === 'payments' ? 'active' : ''}`}
              onClick={() => setActiveTab('payments')}
            >
              <History size={18} /> Payment History
            </button>
            <button 
              className={`tab-btn ${activeTab === 'analytics' ? 'active' : ''}`}
              onClick={() => setActiveTab('analytics')}
              data-testid="tab-analytics"
            >
              <BarChart3 size={18} /> Analytics
            </button>
            <button 
              className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              <Settings size={18} /> Payment Settings
            </button>
            <button 
              className={`tab-btn ${activeTab === 'transactions' ? 'active' : ''}`}
              onClick={() => setActiveTab('transactions')}
            >
              <Activity size={18} /> Legacy Transactions
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'payments' && (
            <div className="tab-content">
              <PaymentHistory />
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="tab-content" data-testid="analytics-tab-content">
              <MerchantAnalytics />
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="tab-content settings-tab" data-testid="settings-tab">
              <h2 className="section-title"><Settings size={20} /> Payment Settings</h2>
              
              {/* Volatility Indicator */}
              {volatilityData && (
                <div className={`volatility-indicator ${volatilityData.volatility_level}`} data-testid="volatility-indicator">
                  <div className="volatility-header">
                    <Activity size={18} />
                    <span>Market Volatility: <strong>{volatilityData.volatility_level.toUpperCase()}</strong></span>
                  </div>
                  <div className="volatility-details">
                    <span>BTC Price: ${volatilityData.btc_price_usd?.toLocaleString() || 'N/A'}</span>
                    <span className="volatility-separator">•</span>
                    <span>24h Change: ~{volatilityData.volatility_24h}%</span>
                    <span className="volatility-separator">•</span>
                    <span className="recommended">Recommended Buffer: <strong>{volatilityData.recommended_buffer}%</strong></span>
                  </div>
                </div>
              )}
              
              {/* Conversion Rate Buffer */}
              <div className="settings-card" data-testid="conversion-buffer-card">
                <div className="settings-card-header">
                  <Shield size={24} className="settings-icon" />
                  <div>
                    <h3>Conversion Rate Protection</h3>
                    <p>Add a buffer percentage to protect against crypto price fluctuations. Your customers pay slightly more, ensuring you receive your full expected amount.</p>
                  </div>
                </div>
                
                <div className="settings-card-content">
                  <div className="buffer-example">
                    <div className="example-row">
                      <span>Your sale amount:</span>
                      <strong>$100.00</strong>
                    </div>
                    <div className="example-row">
                      <span>Buffer ({conversionBuffer}%):</span>
                      <strong>+${(100 * (parseFloat(conversionBuffer) || 0) / 100).toFixed(2)}</strong>
                    </div>
                    <div className="example-row total">
                      <span>Customer pays:</span>
                      <strong>${(100 + (100 * (parseFloat(conversionBuffer) || 0) / 100)).toFixed(2)}</strong>
                    </div>
                  </div>
                  
                  <div className="buffer-input-group">
                    <label>Buffer Percentage</label>
                    <div className="buffer-controls">
                      <div className="buffer-input-wrapper">
                        <input
                          type="number"
                          min="0"
                          max="10"
                          step="0.5"
                          value={conversionBuffer}
                          onChange={(e) => setConversionBuffer(e.target.value)}
                          className="buffer-input"
                          data-testid="conversion-buffer-input"
                        />
                        <span className="buffer-suffix">%</span>
                      </div>
                      {volatilityData && (
                        <button 
                          className="btn-recommended"
                          onClick={applyRecommendedBuffer}
                          data-testid="apply-recommended-btn"
                        >
                          Apply Recommended ({volatilityData.recommended_buffer}%)
                        </button>
                      )}
                    </div>
                    <small>Based on current market: {volatilityData?.message || 'Loading...'}</small>
                  </div>
                  
                  <button 
                    className="btn-primary save-buffer-btn"
                    onClick={handleUpdateConversionBuffer}
                    disabled={savingBuffer}
                    data-testid="save-buffer-btn"
                  >
                    {savingBuffer ? 'Saving...' : 'Save Buffer Setting'}
                  </button>
                </div>
                
                {merchantSettings && (
                  <div className="current-settings">
                    <small>
                      Current buffer: <strong>{merchantSettings.conversion_rate_buffer || 0}%</strong>
                      {merchantSettings.conversion_rate_buffer > 0 && (
                        <span className="buffer-active"> (Active)</span>
                      )}
                    </small>
                  </div>
                )}
              </div>

              {/* Fee Information */}
              <div className="settings-card info-card">
                <div className="settings-card-header">
                  <Percent size={24} className="settings-icon" />
                  <div>
                    <h3>Platform Fee Information</h3>
                    <p>Your current platform fee structure (managed by admin)</p>
                  </div>
                </div>
                <div className="settings-card-content">
                  {merchantSettings && (
                    <div className="fee-info">
                      <div className="fee-row">
                        <span>Platform Fee:</span>
                        <strong>{merchantSettings.platform_fee_percentage || 2}%</strong>
                      </div>
                      <div className="fee-row">
                        <span>Fee Paid By:</span>
                        <strong>{merchantSettings.pass_fee_to_customer ? 'Customer' : 'You (Merchant)'}</strong>
                      </div>
                    </div>
                  )}
                  <small className="fee-note">Contact support to adjust your platform fee percentage.</small>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'transactions' && (
            <div className="transactions-section" data-testid="transactions-section">
              <h2 className="section-title" data-testid="transactions-title">Legacy Transactions (BTCPay)</h2>
              <div className="transactions-table" data-testid="transactions-table">
                {transactions.length === 0 ? (
                  <div className="empty-state" data-testid="empty-state">
                    <CreditCard size={48} color="rgba(228, 230, 235, 0.3)" />
                    <p data-testid="empty-state-text">No legacy transactions</p>
                  </div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((trans, index) => (
                        <tr key={trans.id} data-testid={`transaction-row-${index}`}>
                          <td data-testid={`transaction-date-${index}`}>
                            {new Date(trans.created_at).toLocaleDateString()}
                          </td>
                          <td data-testid={`transaction-amount-${index}`}>
                            ${trans.amount.toFixed(2)} {trans.currency.toUpperCase()}
                          </td>
                          <td data-testid={`transaction-method-${index}`}>
                            <span className="method-badge">{trans.payment_method}</span>
                          </td>
                          <td data-testid={`transaction-status-${index}`}>
                            <span className={`status-badge status-${trans.status}`}>
                              {getStatusIcon(trans.status)}
                              {trans.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onPaymentCreated={(payment) => {
          toast.success('Payment created successfully!');
          fetchData(); // Refresh dashboard data
        }}
      />
    </div>
  );
};

export default Dashboard;